import { useQuery } from "@tanstack/react-query";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { axiosInstance } from "../../utils";
import { queryKeys } from "../queryKeys";
import type { EntityCandidate } from "../models/EntityCandidate";
import { isTextFilterActive, type TextFilterValue } from "../../pages/CandidatesPage/TextFilter";

export interface CandidatesPageRequest {
    page: number;
    pageSize: number;
    sort?: { id: string; desc: boolean }[];
    filters?: Record<string, CandidateColumnFilter>;
    qualificationFilter?: string;
    qualificationFilterMode?: "and" | "or";
}

export interface CandidateColumnFilter {
    type: "text" | "set" | "dateRange" | "numberRange";
    mode?: "contains" | "startsWith" | "equals" | "empty" | "notEmpty";
    value?: string;
    values?: string[];
    from?: string;
    to?: string;
}

export interface CandidatesPageResponse {
    items: EntityCandidate[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

/** Converts TanStack Table state to the wire format expected by POST candidates/search. */
export function buildRequestBody(sorting: SortingState, columnFilters: ColumnFiltersState, page: number, pageSize: number): CandidatesPageRequest {
    const filters: Record<string, CandidateColumnFilter> = {};
    let qualificationFilter: string | undefined;
    let qualificationFilterMode: "and" | "or" | undefined;

    for (const f of columnFilters) {
        if (f.id === "qualifications") {
            // Multi-select qualification filter handled as CSV param
            if (Array.isArray(f.value) && f.value.length > 0) {
                qualificationFilter = (f.value as string[]).join(",");
            }
        } else if (f.id === "qualificationMode") {
            qualificationFilterMode = f.value as "and" | "or";
        } else {
            const val = f.value;
            if (Array.isArray(val)) {
                filters[f.id] = { type: "set", values: val as string[] };
            } else if (typeof val === "string") {
                filters[f.id] = { type: "text", mode: "contains", value: val };
            } else if (val && typeof val === "object" && "mode" in val && isTextFilterActive(val as TextFilterValue)) {
                const tv = val as TextFilterValue;
                if (tv.mode === "empty" || tv.mode === "notEmpty") {
                    filters[f.id] = { type: "text", mode: tv.mode };
                } else {
                    filters[f.id] = { type: "text", mode: tv.mode, value: tv.value };
                }
            } else if (val && typeof val === "object") {
                const asDate = val as { mode?: string; date?: string; from?: string; to?: string };
                if (asDate.mode) {
                    switch (asDate.mode) {
                        case "on":
                            if (asDate.date) filters[f.id] = { type: "dateRange", from: asDate.date, to: asDate.date };
                            break;
                        case "gte":
                            if (asDate.date) filters[f.id] = { type: "dateRange", from: asDate.date };
                            break;
                        case "lte":
                            if (asDate.date) filters[f.id] = { type: "dateRange", to: asDate.date };
                            break;
                        case "range":
                        default:
                            if (asDate.from || asDate.to) filters[f.id] = { type: "dateRange", from: asDate.from, to: asDate.to };
                            break;
                    }
                } else if ("from" in asDate || "to" in asDate) {
                    filters[f.id] = { type: "dateRange", from: asDate.from, to: asDate.to };
                }
            }
        }
    }

    return {
        page,
        pageSize,
        sort: sorting.map(s => ({ id: s.id, desc: s.desc })),
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        qualificationFilter,
        qualificationFilterMode,
    };
}

export function useCandidatesPage(
    clientId: string,
    entityId: string | undefined,
    page: number,
    pageSize: number,
    sorting: SortingState,
    columnFilters: ColumnFiltersState
) {
    const axios = axiosInstance();
    const sortKey = sorting.map(s => `${s.id}:${s.desc}`).join(",");
    const filtersKey = JSON.stringify(columnFilters);
    const qualFilter = columnFilters.find(f => f.id === "qualifications");
    const qualFilterKey = qualFilter && Array.isArray(qualFilter.value) ? (qualFilter.value as string[]).join(",") : "";

    return useQuery({
        ...queryKeys.candidatesPage.search(clientId, entityId, page, pageSize, sortKey, filtersKey, qualFilterKey),
        queryFn: () => {
            const body = buildRequestBody(sorting, columnFilters, page, pageSize);
            return axios.post<CandidatesPageResponse>(`api/Clients/${clientId}/Entities/${entityId ?? "all"}/candidates/search`, body).then(r => r.data);
        },
        keepPreviousData: true,
    });
}
